<template>
  <v-widget
    dark
    color="sand"
    class="pa-4 pb-0 account-total-card d-flex flex-column justify-center"
    v-bind="$attrs"
  >
    <template #content>
      <v-row dense>
        <v-col cols="12" class="d-flex flex-row flex-wrap gap-2">
          <div>
            <div class="text-subtitle font-weight-medium primary--text mb-4">
              {{ $t('portal_total_assets') }}
            </div>
            <p class="text-title black--text font-weight-bold">
              <span :class="getAmountClassPrimary(netAssets)">
                $ {{ netAssets | currency }}
              </span>
            </p>
          </div>
          <div>
            <div class="text-subtitle font-weight-medium primary--text mb-4">
              {{ $t('portal_today_change') }}
            </div>
            <p class="text-title font-weight-bold text-exchange">
              <span :class="getAmountClassGreen(lastDayChangeUSD)">
                $
              </span>
              <span :class="getAmountClassGreen(lastDayChangeUSD)">
                {{ lastDayChangeUSD | currency }}
              </span>
              <span :class="getAmountClassGreen(lastDayChangeUSD)">
                /
              </span>
              <span :class="getAmountClassGreen(lastDayChangeUSD)">
                {{ lastDayChangePercent }} %
              </span>
            </p>
          </div>
          <div v-if="lastDayCashAndTitleChangeUSD !== null">
            <div class="text-subtitle font-weight-medium primary--text mb-4">
              {{ $t('portal_daily_net_flows') }}
            </div>
            <p class="text-title black--text font-weight-bold">
              <span :class="getAmountClassPrimary(lastDayCashAndTitleChangeUSD)">
                $ {{ lastDayCashAndTitleChangeUSD | currency }}
              </span>
            </p>
          </div>
        </v-col>
      </v-row>

      <v-row dense class="d-flex flex-row gap mt-0">
        <v-col cols="12" sm="5" class="d-flex justify-start">
          <span class="text-subtitle text-decoration-none font-weight-medium primary--text">
            {{ $t('portal_info_at') }}
            {{ lastWorkingDate }}
          </span>
        </v-col>
        <v-col
          cols="12"
          sm="7"
          class="d-flex gap justify-space-between justify-sm-end align-start d-print-none"
        >
          <!-- STATEMENT ACCOUNTS -->
          <a
            @click="handleAccountStatusModal"
            class="mr-sm-2 mr-md-6 d-flex align-center"
            v-if="hasPermission('view-statement-accounts')"
          >
            <v-icon
              class="mr-1 mr-sm-2"
              v-text="'mdi-file-download'"
              color="primary"
              size="18"
            />
            <span class="text-description font-weight-medium text-body-2">{{ $t('portal_account_state') }}</span>
          </a>
          <!-- INVOICES -->
          <a
            @click="handleInvoiceModal"
            class="mr-sm-2 mr-md-6 d-flex align-center"
            v-if="hasPermission('view-statement-accounts')"
          >
            <v-icon
              class="mr-1 mr-sm-2"
              v-text="'mdi-file-document'"
              color="primary"
              size="18"
            />
            <span class="text-description font-weight-medium text-body-2">{{ $t('portal_invoices') }}</span>
          </a>
          <!-- PRINT -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <a
                @click="handlePrint"
                class="mr-sm-2 mr-md-6 d-flex align-center"
                v-if="hasPermission('view-account-print')"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  class="mr-1 mr-sm-2"
                  v-text="'mdi-printer'"
                  color="primary"
                  size="18"
                />
                <span class="text-description font-weight-medium text-body-2">{{ $t('portal_print') }}</span>
              </a>
            </template>
            <span>{{ $t('portal_print_message') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </template>
  </v-widget>
</template>

<script>
import VWidget from '@/components/commons/VWidget'
import LastDayPerformanceService from '@/services/lastDayPerformance'
import StatusAccountModal from './StatusAccountModal'
import InvoiceModal from './InvoiceModal'
import { mapState } from 'vuex'
import { hasPermission } from '@/utils'

export default {
  components: { VWidget },
  props: {},
  computed: {
    ...mapState('account', ['account']),
    ...mapState('language', ['languages', 'idiom']),
    ...mapState('auth', ['user']),
    items() {
      return this.languages
        .filter(item => item.code !== this.idiom.code)
        .map(item => {
          return {
            ...item,
            title: item.name
          }
        })
    },
    dateYesterday() {
      let yesterday = (d => new Date(d.setDate(d.getDate() - 1)))(new Date())
      let month =
        yesterday.getMonth() + 1 < 10
          ? `0${yesterday.getMonth() + 1}`
          : yesterday.getMonth() + 1 //months from 1-12
      let day =
        yesterday.getDate() < 10
          ? `0${yesterday.getDate()}`
          : yesterday.getDate()
      let year = yesterday.getFullYear()
      if (this.idiom.code === 'en') {
        return `${month}/${day}/${year}c`
      } else {
        return `${day}/${month}/${year}c`
      }
    }
  },
  data() {
    return {
      netAssets: 0,
      totalAssets: 0,
      lastDayChangeUSD: 0,
      lastDayChangePercent: 0,
      lastWorkingDate: '',
      lastDayCashAndTitleChangeUSD: null,
      data: {},
      loading: false
    }
  },
  created() {
    this.populateProducts()
  },
  watch: {
    async account() {
      await this.populateProducts()
    }
  },
  methods: {
    async populateProducts() {
      this.loading = true
      try {
        const data = await LastDayPerformanceService.find(this.account.code)
        this.netAssets = data.netAssets
        this.totalAssets = data.totalAssets
        this.lastDayChangeUSD = data.lastDayChangeUSD
        this.lastDayChangePercent = data.lastDayChangePercent
        this.lastWorkingDate = data.lastWorkingDate
        this.lastDayCashAndTitleChangeUSD = data.lastDayCashAndTitleChangeUSD ?? null
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },

    hasPermission(permission) {
      return hasPermission(this.user, permission)
    },

    async handleAccountStatusModal() {
      const params = {
        fullscreen: this.$vuetify.breakpoint.smAndDown,
        width: '50%',
        // scrollable: true,
        transition: 'dialog-top-transition',
        showClose: false
      }

      await this.$dialog.showAndWait(StatusAccountModal, params)
    },

    async handleInvoiceModal() {
      const params = {
        fullscreen: this.$vuetify.breakpoint.smAndDown,
        width: '50%',
        transition: 'dialog-top-transition',
        showClose: false
      }

      await this.$dialog.showAndWait(InvoiceModal, params)
    },

    handlePrint() {
      window.print()
    },

    getAmountClassPrimary(amount) {
      return parseFloat(amount) >= 0 ? 'primary--text' : 'error--text'
    },

    getAmountClassGreen(amount) {
      return parseFloat(amount) >= 0 ? 'green--text' : 'error--text'
    }
  }
}
</script>

<style lang="sass" scoped>
.account-total-card
  .gap-2
    column-gap: 2rem

  .text-subtitle
    font-size: 1rem
    color: var(--v-silver-base)

  .text-description
    font-size: 1rem
    line-height: normal
    color: var(--v-primary-base)
    &:hover
      color: var(--v-primary-base)
      text-decoration: underline

.gap
  gap: 1rem

.text-exchange
  color: black

.text-title
  font-size: 1rem

.date
  color: #00559e

@media screen and (min-width: 500px)
  .text-description
    font-size: 0.9rem

  .text-subtitle
    font-size: 1rem


  //.text-title
  //  font-size: 1.714rem

@media screen and (min-width: 600px)
  .gap
    gap: 0
  .text-description
    font-size: 1rem

@media screen and (min-width: 772px)
  .text-title
    font-size: 1.714rem
</style>
